import { useState, useEffect } from "react";
import { Plus, X, Trash2, Loader2, Sparkles, AlertTriangle, Image as ImageIcon, Repeat, ChevronUp, ChevronDown, Eye } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlanillaPreview } from "@/components/planillas/PlanillaPreview";
import { api, apiErr, fileUrl } from "@/lib/api";

const TYPES = [
  ["instruction", "Texto / instrucción (sin respuesta)"],
  ["compliance_yesno", "Sí / No"], ["compliance_yesnona", "Sí / No / N/A"],
  ["cumple", "Cumple / No cumple"], ["cumple_na", "Cumple / No cumple / N/A"],
  ["text", "Texto corto"], ["textarea", "Texto largo"], ["number", "Número"], ["temperature", "Temperatura"],
  ["date", "Fecha"], ["time", "Hora"], ["select", "Lista desplegable"], ["file", "Archivo / documento"],
];
const COMPLIANCE_TYPES = ["compliance_yesno", "compliance_yesnona", "cumple", "cumple_na"];
const NC_TOKEN = { compliance_yesno: "No", compliance_yesnona: "No", cumple: "No cumple", cumple_na: "No cumple" };
const EMPTY = { name: "", description: "", category: "Otro", frequency: "DIARIA", target_time: "",
  tolerance_minutes: 60, evidence_required: false, generates_incident: true, active: true,
  location_ids: [], area_ids: [], sections: [] };

export function TemplateBuilder({ templateId, open, onClose, onSaved, cats, initialDraft, importId, reviewMode }) {
  const [t, setT] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [refImage, setRefImage] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initialDraft) {
      const sections = (initialDraft.sections || []).map((s, i) => ({
        id: `s${Date.now()}-${i}`, title: s.title || `Sección ${i + 1}`,
        repeatable: !!s.repeatable, singular_label: s.singular_label || "", _ai_repeatable: !!s.repeatable,
        questions: (s.questions || []).map((q, j) => ({
          id: `q${Date.now()}-${i}-${j}`, type: q.type || "text", label: q.label || "",
          required: q.required !== false, photo_required: !!q.photo_required,
          options: q.options || [], min: q.min ?? null, max: q.max ?? null, unit: q.unit || "",
          observation_on: q.observation_on || null, help_text: q.help_text || "", style: q.style || null,
          _confidence: q._confidence, _source_text: q._source_text,
        })),
      }));
      setT({ ...EMPTY, name: initialDraft.name || "", description: initialDraft.description || "",
        category: initialDraft.category || "Otro", sections });
      setRefImage(null);
    } else if (templateId) {
      api.get(`/planillas/templates/${templateId}`).then(({ data }) => {
        setT({ ...EMPTY, ...data });
        setRefImage(data.reference_image_path || null);
      }).catch(() => {});
    } else {
      setT({ ...EMPTY, sections: [{ id: `s${Date.now()}`, title: "Sección 1", repeatable: false, singular_label: "", questions: [] }] });
      setRefImage(null);
    }
  }, [templateId, open, initialDraft]);

  const upd = (patch) => setT((p) => ({ ...p, ...patch }));
  const updSec = (i, patch) => setT((p) => ({ ...p, sections: p.sections.map((s, j) => j === i ? { ...s, ...patch } : s) }));
  const addSec = () => upd({ sections: [...t.sections, { id: `s${Date.now()}`, title: `Sección ${t.sections.length + 1}`, repeatable: false, singular_label: "", questions: [] }] });
  const delSec = (i) => upd({ sections: t.sections.filter((_, j) => j !== i) });
  const moveSec = (i, dir) => setT((p) => { const a = [...p.sections]; const j = i + dir; if (j < 0 || j >= a.length) return p; [a[i], a[j]] = [a[j], a[i]]; return { ...p, sections: a }; });
  const addQ = (i) => updSec(i, { questions: [...t.sections[i].questions, { id: `q${Date.now()}`, type: "compliance_yesno", label: "", required: true, photo_required: false, options: [] }] });
  const updQ = (si, qi, patch) => updSec(si, { questions: t.sections[si].questions.map((q, k) => k === qi ? { ...q, ...patch } : q) });
  const delQ = (si, qi) => updSec(si, { questions: t.sections[si].questions.filter((_, k) => k !== qi) });
  const moveQ = (si, qi, dir) => setT((p) => { const secs = p.sections.map((s) => ({ ...s, questions: [...s.questions] })); const qs = secs[si].questions; const j = qi + dir; if (j < 0 || j >= qs.length) return p; [qs[qi], qs[j]] = [qs[j], qs[qi]]; return { ...p, sections: secs }; });

  const deleteRefImage = async () => {
    try { await api.delete(`/planillas/templates/${templateId}/reference-image`); setRefImage(null); toast.success("Imagen de referencia eliminada"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const save = async () => {
    if (!t.name) { toast.error("Nombre requerido"); return; }
    setSaving(true);
    try {
      const sections = t.sections.map((s) => ({
        id: s.id, title: s.title, repeatable: !!s.repeatable,
        singular_label: s.repeatable ? (s.singular_label || null) : null,
        questions: s.questions.map((q) => ({
          id: q.id, type: q.type, label: q.label,
          required: q.type === "instruction" ? false : q.required,
          photo_required: q.type === "instruction" ? false : q.photo_required,
          options: q.options || [], min: q.min ?? null, max: q.max ?? null, unit: q.unit || null,
          observation_on: q.observation_on || null,
          help_text: q.help_text || null, style: q.style || null,
        })),
      }));
      const payload = { ...t, sections, tolerance_minutes: Number(t.tolerance_minutes) || 60,
        target_time: t.target_time || null, reference_import_id: importId || null };
      if (templateId) await api.put(`/planillas/templates/${templateId}`, payload);
      else await api.post("/planillas/templates", payload);
      toast.success("Plantilla guardada"); onSaved(); onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const renderQuestion = (q, si, qi, count) => {
    const lowConf = typeof q._confidence === "number" && q._confidence < 0.6;
    const hasRange = ["number", "temperature"].includes(q.type) && (q.min != null || q.max != null);
    const isInstruction = q.type === "instruction";
    const isCompliance = COMPLIANCE_TYPES.includes(q.type);
    return (
      <div key={q.id} className={`rounded-lg p-3 ${isInstruction ? "border border-blue-100 bg-blue-50/50" : "bg-[#F8FAFC]"}`}>
        <div className="flex items-center gap-1">
          <Input data-testid={`q-label-${si}-${qi}`} value={q.label} onChange={(e) => updQ(si, qi, { label: e.target.value })} className="h-10" placeholder={isInstruction ? "Título (opcional)" : "Pregunta"} />
          <Button variant="ghost" size="icon" className="h-8 w-8" disabled={qi === 0} onClick={() => moveQ(si, qi, -1)} data-testid={`q-up-${si}-${qi}`}><ChevronUp className="h-4 w-4 text-slate-400" /></Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" disabled={qi === count - 1} onClick={() => moveQ(si, qi, 1)} data-testid={`q-down-${si}-${qi}`}><ChevronDown className="h-4 w-4 text-slate-400" /></Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => delQ(si, qi)}><X className="h-4 w-4 text-slate-400" /></Button>
        </div>
        {(lowConf || (reviewMode && hasRange)) && (
          <div className="mt-1.5 flex flex-wrap gap-2">
            {lowConf && <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700"><AlertTriangle className="h-3 w-3" /> Revisar</span>}
            {reviewMode && hasRange && <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700"><AlertTriangle className="h-3 w-3" /> Confirmar rango {q.min ?? "?"}–{q.max ?? "?"}{q.unit ? ` ${q.unit}` : ""}</span>}
          </div>
        )}
        <div className="mt-2 grid grid-cols-2 gap-2">
          <Select value={q.type} onValueChange={(v) => updQ(si, qi, { type: v })}>
            <SelectTrigger data-testid={`q-type-${si}-${qi}`} className="h-9 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
          {["number", "temperature"].includes(q.type) && (
            <div className="flex gap-1">
              <Input value={q.min ?? ""} onChange={(e) => updQ(si, qi, { min: e.target.value === "" ? null : Number(e.target.value) })} className="h-9 tnum text-xs" placeholder="Mín" />
              <Input value={q.max ?? ""} onChange={(e) => updQ(si, qi, { max: e.target.value === "" ? null : Number(e.target.value) })} className="h-9 tnum text-xs" placeholder="Máx" />
              <Input value={q.unit ?? ""} onChange={(e) => updQ(si, qi, { unit: e.target.value })} className="h-9 w-14 text-xs" placeholder="°C" />
            </div>
          )}
          {q.type === "select" && (
            <Input value={(q.options || []).join(", ")} onChange={(e) => updQ(si, qi, { options: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} className="h-9 text-xs" placeholder="Opciones separadas por coma" />
          )}
          {isInstruction && (
            <Select value={q.style || "info"} onValueChange={(v) => updQ(si, qi, { style: v })}>
              <SelectTrigger data-testid={`q-style-${si}-${qi}`} className="h-9 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="info">Informativo</SelectItem><SelectItem value="warning">Advertencia</SelectItem></SelectContent>
            </Select>
          )}
        </div>
        {isInstruction ? (
          <Textarea data-testid={`q-instruction-body-${si}-${qi}`} value={q.help_text || ""} onChange={(e) => updQ(si, qi, { help_text: e.target.value })} className="mt-2 text-sm" placeholder="Texto / contenido mostrado al operario (procedimiento, límite, indicación…)" />
        ) : (
          <div className="mt-2 flex flex-wrap gap-3 text-xs">
            <label className="flex items-center gap-1.5"><Checkbox checked={q.required} onCheckedChange={(v) => updQ(si, qi, { required: !!v })} /> {q.type === "file" ? "Archivo obligatorio" : "Obligatoria"}</label>
            {q.type !== "file" && <label className="flex items-center gap-1.5"><Checkbox checked={q.photo_required} onCheckedChange={(v) => updQ(si, qi, { photo_required: !!v })} /> Foto obligatoria</label>}
            {isCompliance && (
              <label className="flex items-center gap-1.5" data-testid={`q-obsrule-${si}-${qi}`}>
                <Checkbox checked={q.observation_on === NC_TOKEN[q.type]} onCheckedChange={(v) => updQ(si, qi, { observation_on: v ? NC_TOKEN[q.type] : null })} />
                Observación obligatoria al incumplir
              </label>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-display">{templateId ? "Editar plantilla" : "Nueva plantilla"}</DialogTitle></DialogHeader>

        {reviewMode && (
          <div className="flex items-start gap-2 rounded-xl border border-blue-200 bg-blue-50 p-3 text-sm text-[#1D4ED8]" data-testid="review-banner">
            <Sparkles className="mt-0.5 h-4 w-4 shrink-0" />
            <span>Borrador generado con IA. <b>Revisa la estructura antes de guardar.</b> La IA puede interpretar incorrectamente algunos campos.</span>
          </div>
        )}

        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label className="text-sm">Nombre *</Label><Input data-testid="tpl-name" value={t.name} onChange={(e) => upd({ name: e.target.value })} className="mt-1 h-11" /></div>
            <div><Label className="text-sm">Categoría</Label>
              <Select value={t.category} onValueChange={(v) => upd({ category: v })}>
                <SelectTrigger data-testid="tpl-category" className="mt-1 h-11"><SelectValue /></SelectTrigger>
                <SelectContent>{(cats || ["Otro"]).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-sm">Frecuencia</Label>
              <Select value={t.frequency} onValueChange={(v) => upd({ frequency: v })}>
                <SelectTrigger data-testid="tpl-frequency" className="mt-1 h-11"><SelectValue /></SelectTrigger>
                <SelectContent>{["SIN_PROGRAMACION", "DIARIA", "POR_TURNO", "SEMANAL", "MENSUAL"].map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-sm">Hora objetivo</Label><Input type="time" value={t.target_time || ""} onChange={(e) => upd({ target_time: e.target.value })} className="mt-1 h-11" /></div>
          </div>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={t.evidence_required} onCheckedChange={(v) => upd({ evidence_required: !!v })} /> Evidencia obligatoria</label>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={t.generates_incident} onCheckedChange={(v) => upd({ generates_incident: !!v })} /> Genera incidencia ante incumplimiento</label>
          </div>

          {refImage && (
            <div className="flex items-center gap-3 rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-3" data-testid="reference-image-block">
              <img src={fileUrl(refImage)} alt="Documento de referencia" className="h-16 w-16 rounded-lg object-cover" />
              <div className="min-w-0 flex-1"><p className="text-sm font-medium text-[#153D35]">Documento de referencia</p>
                <a href={fileUrl(refImage)} target="_blank" rel="noreferrer" className="text-xs text-[#2563EB] hover:underline">Ver imagen original</a></div>
              <Button variant="ghost" size="icon" data-testid="delete-reference-btn" onClick={deleteRefImage}><Trash2 className="h-4 w-4 text-slate-400" /></Button>
            </div>
          )}

          {t.sections.map((sec, si) => (
            <div key={sec.id} data-testid={`section-${si}`} className="rounded-xl border border-[#E2E8F0] p-3">
              <div className="flex items-center gap-1">
                <Input value={sec.title} onChange={(e) => updSec(si, { title: e.target.value })} className="h-10 font-medium" placeholder="Título de sección" />
                <Button variant="ghost" size="icon" className="h-8 w-8" disabled={si === 0} onClick={() => moveSec(si, -1)} data-testid={`sec-up-${si}`}><ChevronUp className="h-4 w-4 text-slate-400" /></Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" disabled={si === t.sections.length - 1} onClick={() => moveSec(si, 1)} data-testid={`sec-down-${si}`}><ChevronDown className="h-4 w-4 text-slate-400" /></Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => delSec(si)}><Trash2 className="h-4 w-4 text-slate-400" /></Button>
              </div>

              <div className="mt-2 flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-1.5 text-xs text-[#153D35]">
                  <Checkbox data-testid={`sec-repeatable-${si}`} checked={!!sec.repeatable} onCheckedChange={(v) => updSec(si, { repeatable: !!v })} />
                  <Repeat className="h-3.5 w-3.5" /> Sección repetible
                </label>
                {sec._ai_repeatable && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-medium text-[#1D4ED8]">
                    <Sparkles className="h-3 w-3" /> Sugerencia de IA: sección repetible
                  </span>
                )}
                {sec.repeatable && (
                  <Input data-testid={`sec-singular-${si}`} value={sec.singular_label || ""} onChange={(e) => updSec(si, { singular_label: e.target.value })}
                    className="h-9 w-56 text-xs" placeholder="Nombre en singular (ej: Trabajador)" />
                )}
              </div>

              <div className="mt-3 space-y-3">
                {sec.questions.map((q, qi) => renderQuestion(q, si, qi, sec.questions.length))}
                <Button variant="outline" size="sm" onClick={() => addQ(si)} className="rounded-lg"><Plus className="mr-1 h-3.5 w-3.5" /> Pregunta</Button>
              </div>
            </div>
          ))}
          <Button variant="outline" onClick={addSec} className="rounded-xl"><Plus className="mr-2 h-4 w-4" /> Agregar sección</Button>
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" data-testid="preview-template-btn" onClick={() => setShowPreview(true)} className="rounded-xl">
            <Eye className="mr-2 h-4 w-4" /> Previsualizar
          </Button>
          <Button data-testid="save-template-btn" disabled={saving} onClick={save} className="rounded-xl bg-[#2563EB] hover:bg-[#1D4ED8]">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar plantilla"}</Button>
        </DialogFooter>
        <PlanillaPreview open={showPreview} onClose={() => setShowPreview(false)} template={t} />
      </DialogContent>
    </Dialog>
  );
}
